const WIDGET_MOUNT_DELAY_MS = 200;
const PLACEHOLDER_SELECTOR = '#import-proficiency-placeholder';

class ImportProficiencyPopupController {
    constructor($timeout, $q, $log, ertBasePopupService, loadingIndicatorService, reportsService) {
        'ngInject';
        this.$timeout = $timeout;
        this.$q = $q;
        this.$log = $log;
        this.ertBasePopupService = ertBasePopupService;
        this.loadingIndicatorService = loadingIndicatorService;
        this.reportsService = reportsService;

        this.$API = null;
        this.heading = 'reports.training-document.proficiencyLabel';
        this.resetState();
    }

    $onInit() {
        this.initButtons();
        this.$API = this.buildApi();
        this.componentReady({ $API: this.$API });
        try {
            this.ertBasePopupService.register('importProficiency', this.$API);
        } catch (e) {
            this.$log.debug('ertBasePopupService.register not available', e);
        }
    }

    resetState() {
        this.open = false;
        this.isLoading = false;
        this.actions = [];
        this.isFormValid = false;
        this.formData = null;
        this.currentStudyId = null;
        this.step = 1;
        this.pin = '';
        this.protocol = '';
    }

    fetchUserCertifications(loginId, studyId, siteId) {
        return this.reportsService.getUserCertifications(loginId, studyId, siteId)
            .then((response) => response.data);
    }

    formatDate(isoDate) {
        if (!isoDate) { return ''; }
        const d = new Date(isoDate);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
    }

    formatOperatorName(record) {
        const name = (record.operatorName || '').trim() || '-';
        const username = record.loginId || '';
        return `${name} (${username})`;
    }

    mapRecords(records) {
        return (records || []).map((r) => ({
            studyId: r.studyId,
            siteId: r.siteId,
            protocol: r.studyName,
            site: r.sponsorSiteNumber,
            certificateDate: this.formatDate(r.certificationDate),
            operatorName: this.formatOperatorName(r),
            operatorFirstName: r.operatorFirstName,
            operatorLastName: r.operatorLastName,
            reason: r.reason || undefined
        }));
    }

    fetchData() {
        return this.$q.when(null);
    }

    cancelAction() {
        if (this.$API) {
            this.$API.close();
        } else {
            this.open = false;
        }
    }

    goToStep(step) {
        this.step = step;
        if (step === 1) {
            this.actions = this.buildStep1Actions();
        } else if (step === 2) {
            // Submit starts disabled — enabled only after user selects an eligible record
            this.submitBtn.isDisabled = true;
            this.isFormValid = false;
            this.actions = [this.submitBtn, this.backBtn];
        } else if (step === 3) {
            this.actions = [this.closeBtn];
        }
    }

    buildStep1Actions() {
        return [
            Object.assign({}, this.nextBtn, { isDisabled: !this.isFormValid }),
            this.cancelBtn
        ];
    }

    initButtons() {
        this.nextBtn = {
            action: () => {
                if (!this.isFormValid) { return; }

                const loginId = (this.formData && this.formData.username ? this.formData.username.trim() : '') || '';
                const studyId = this.currentStudyId;
                const siteId = (this.formData && this.formData.site ? String(this.formData.site).trim() : '') || '';

                if (!loginId || !studyId || !siteId) {
                    this.$log.error('Cannot fetch user certifications: missing loginId, studyId, or siteId');
                    return;
                }

                this.isLoading = true;
                this.loadingIndicatorService.show();

                this.fetchUserCertifications(loginId, studyId, siteId)
                    .then((data) => {
                        const eligibleRecords = this.mapRecords(data && data.eligible);
                        const ineligibleRecords = this.mapRecords(data && data.ineligible);
                        const hasExistingProficiency = !!(data && data.hasExistingProficiencyForCurrentProtocolSite);
                        const existingProficiencyPin = (data && data.existingProficiencyPin) || '';

                        if (this.widgetEl && this.widgetEl[0]) {
                            this.widgetEl[0].eligibleRecords = eligibleRecords;
                            this.widgetEl[0].ineligibleRecords = ineligibleRecords;
                            this.widgetEl[0].hasExistingProficiency = hasExistingProficiency;
                            this.widgetEl[0].protocol = this.protocol || '';
                            this.widgetEl[0].pin = hasExistingProficiency ? existingProficiencyPin : '';
                            this.widgetEl[0].currentStep = 2;
                        }

                        if (hasExistingProficiency) {
                            this.protocol = this.protocol || '';
                            this.pin = existingProficiencyPin;
                            this.goToStep(3);
                        } else {
                            this.goToStep(2);
                        }
                    })
                    .catch((err) => {
                        this.$log.error('Failed to load user certifications', err);

                        if (this.widgetEl && this.widgetEl[0]) {
                            this.widgetEl[0].eligibleRecords = [];
                            this.widgetEl[0].ineligibleRecords = [];
                            this.widgetEl[0].hasExistingProficiency = false;
                            this.widgetEl[0].pin = '';
                            this.widgetEl[0].currentStep = 2;
                        }
                        this.goToStep(2);
                    })
                    .then(() => {
                        this.isLoading = false;
                        this.loadingIndicatorService.hide();
                    });
            },
            cssClass: '-es-primary',
            displayName: 'app.buttons.next',
            isDisabled: true,
            type: 'submit'
        };

        this.cancelBtn = {
            action: () => this.$API.close(),
            displayName: 'app.buttons.cancel',
            isDisabled: false
        };

        this.backBtn = {
            action: () => {
                if (this.widgetEl && this.widgetEl[0]) {
                    this.widgetEl[0].hasExistingProficiency = false;
                    this.widgetEl[0].pin = '';
                    this.widgetEl[0].currentStep = 1;
                }
                this.goToStep(1);
            },
            displayName: 'app.buttons.back',
            isDisabled: false
        };

        this.submitBtn = {
            action: () => this.fetchData().then(() => {
                if (this.onSubmit) {
                    this.onSubmit({ result: this.formData });
                }
            }),
            cssClass: '-es-primary',
            displayName: 'app.buttons.submit',
            isDisabled: false,
            type: 'submit'
        };

        this.closeBtn = {
            action: () => this.$API.close(),
            cssClass: '-es-primary',
            displayName: 'OK',
            isDisabled: false
        };
    }

    updateActions() {
        this.$timeout(() => {
            if (this.step === 1) {
                this.actions = this.buildStep1Actions();
            } else if (this.step === 2) {
                this.submitBtn.isDisabled = !this.isFormValid;
                this.actions = [this.submitBtn, this.backBtn];
            }
        });
    }

    mapSites(sites) {
        return (sites || []).map((s) => {
            if (angular.isString(s)) { return s; }
            return s.sponsorSiteId || s.displaySiteName || '';
        });
    }

    buildSiteLookup(sites) {
        const lookup = {};
        (sites || []).forEach((s) => {
            if (angular.isString(s)) { return; }
            const display = s.sponsorSiteId || s.displaySiteName || '';
            if (display && s.siteId != null) {
                lookup[display] = s.siteId;
            }
        });
        return lookup;
    }

    mountWidget(protocol, sites, eligibleRecords, ineligibleRecords) {
        const placeholderEl = angular.element(document).find(PLACEHOLDER_SELECTOR);
        const widgetEl = angular.element('<import-proficiency />');
        this.widgetEl = widgetEl;

        widgetEl[0].addEventListener('formDataChange', (event) => {
            this.formData = event.detail;
        });

        widgetEl[0].addEventListener('formValidationChange', (event) => {
            this.isFormValid = !!event.detail;
            this.updateActions();
        });

        widgetEl[0].protocol = protocol || '';
        widgetEl[0].hasExistingProficiency = false;
        widgetEl[0].pin = '';
        widgetEl[0].sites = this.mapSites(sites);
        widgetEl[0].siteLookup = this.buildSiteLookup(sites);
        widgetEl[0].eligibleRecords = eligibleRecords || [];
        widgetEl[0].ineligibleRecords = ineligibleRecords || [];

        placeholderEl.empty().append(widgetEl);
    }

    buildApi() {
        return {
            open: (protocol, sites, studyId) => {
                this.isLoading = true;
                this.loadingIndicatorService.show();
                this.open = true;
                this.protocol = protocol || '';
                this.pin = '';
                this.currentStudyId = studyId;
                this.goToStep(1);

                this.$timeout(() => {
                    this.mountWidget(protocol, sites, [], []);
                    this.isLoading = false;
                    this.loadingIndicatorService.hide();
                }, WIDGET_MOUNT_DELAY_MS);
            },
            close: () => {
                this.resetState();
                const placeholderEl = angular.element(document).find(PLACEHOLDER_SELECTOR);
                placeholderEl.empty();
            },
            showPinResult: (protocol, pin) => {
                this.protocol = protocol;
                this.pin = pin;
                if (this.widgetEl && this.widgetEl[0]) {
                    this.widgetEl[0].currentStep = 3;
                    this.widgetEl[0].hasExistingProficiency = false;
                    this.widgetEl[0].protocol = protocol;
                    this.widgetEl[0].pin = pin;
                }
                this.goToStep(3);
            },
            getElement: () => (this.widgetEl ? this.widgetEl[0] : null)
        };
    }
}

export default ImportProficiencyPopupController;
